import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { reportApi, lcClassApi, type LearnerProgressDetail, type DashboardClassDetail, type CurriculumProgress, type ProductProgressReport } from '@/api/b2b'
import {
  Download, Users, Search, ArrowUpDown, ArrowUp, ArrowDown,
  Building2, Package, User, GraduationCap, MessageSquare,
  BookOpen
} from 'lucide-react'
import { LearnerProgressDetailModal } from '@/components/admin/LearnerProgressDetailModal'
import { toast } from 'sonner'
import { useAuthStore } from '@/store/authStore'
import { isDevToolsVisible } from '@/utils/devTools'
import { isAiqReportDateLocked } from '@/config/aiqReportGate'

type TabType = 'company' | 'lcClass' | 'learner' | 'product'

export default function ProgressReport() {
  const { t } = useTranslation('admin')
  const [activeTab, setActiveTab] = useState<TabType>('learner')
  const [selectedClassId, setSelectedClassId] = useState<number | undefined>()

  // 커리큘럼별 리포트
  const { data: curriculumReport, isLoading: isLoadingCompany } = useQuery({
    queryKey: ['progressReport', 'curriculum'],
    queryFn: () => reportApi.getCurriculumProgressReport(),
    enabled: activeTab === 'company',
  })

  // 상품별 리포트
  const { data: productReport, isLoading: isLoadingProduct } = useQuery({
    queryKey: ['progressReport', 'product'],
    queryFn: () => reportApi.getProductProgressReport(),
    enabled: activeTab === 'product',
  })

  // 클래스별 리포트 - 대시보드와 동일한 API 사용
  const { data: classDetailsData, isLoading: isLoadingClassDetails } = useQuery({
    queryKey: ['progressReport', 'classDetails'],
    queryFn: () => reportApi.getDashboardClassDetails(),
    enabled: activeTab === 'lcClass',
  })

  const handleExportExcel = () => {
    toast.info(t('progress.toast.excelExportPhase2'))
  }

  const isLoadingLcClass = isLoadingClassDetails
  const isLoading = isLoadingCompany || isLoadingLcClass || isLoadingProduct

  const handleNavigateToLearnersByClass = (classId: number) => {
    setSelectedClassId(classId)
    setActiveTab('learner')
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('progress.title')}</h1>
          <p className="mt-1 text-gray-500">{t('progress.description')}</p>
        </div>
        {/* <button
          onClick={handleExportExcel}
          className="flex items-center px-4 py-2 space-x-2 text-white bg-green-600 rounded-lg hover:bg-green-700"
        >
          <Download className="w-5 h-5" />
          <span>Excel 다운로드</span>
        </button> */}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('learner')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'learner'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4" />
                <span>{t('progress.tabs.learner')}</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('lcClass')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'lcClass'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <GraduationCap className="w-4 h-4" />
                <span>{t('progress.tabs.class')}</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('product')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'product'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Package className="w-4 h-4" />
                <span>{t('progress.tabs.product')}</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('company')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'company'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Building2 className="w-4 h-4" />
                <span>{t('progress.tabs.curriculum')}</span>
              </div>
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'company' && (
            isLoadingCompany ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-gray-500">{t('common.loading')}</div>
              </div>
            ) : (
              <CompanyView curricula={curriculumReport?.data?.data || []} />
            )
          )}
          {activeTab === 'lcClass' && (
            isLoadingLcClass ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-gray-500">{t('common.loading')}</div>
              </div>
            ) : (
              <LcClassView
                classDetails={classDetailsData?.data?.data || []}
                onNavigateToLearners={handleNavigateToLearnersByClass}
              />
            )
          )}
          {activeTab === 'product' && (
            isLoadingProduct ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-gray-500">{t('common.loading')}</div>
              </div>
            ) : (
              <ProductView products={productReport?.data?.data || []} />
            )
          )}
          {activeTab === 'learner' && (
            <LearnerView
              initialClassId={selectedClassId}
              onClassIdConsumed={() => setSelectedClassId(undefined)}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// 커리큘럼별 뷰
function CompanyView({ curricula }: { curricula: CurriculumProgress[] }) {
  const { t } = useTranslation('admin')
  if (curricula.length === 0) {
    return <div className="py-8 text-center text-gray-500">{t('progress.curriculum.empty')}</div>
  }

  return (
    <div className="space-y-4">
      <div className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">{t('progress.curriculum.name')}</th>
              <th className="px-6 py-3 text-xs font-medium text-center text-gray-500 uppercase">{t('progress.curriculum.totalLessons')}</th>
              <th className="px-6 py-3 text-xs font-medium text-center text-gray-500 uppercase">{t('progress.curriculum.inProgress')}</th>
              <th className="px-6 py-3 text-xs font-medium text-center text-gray-500 uppercase">{t('progress.curriculum.completed')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {curricula.map((c) => (
              <tr key={c.curriculumId} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{c.curriculumTitle}</td>
                <td className="px-6 py-4 text-sm text-center text-gray-700">{c.totalLessons}</td>
                <td className="px-6 py-4 text-center">
                  <span className="inline-flex px-2.5 py-0.5 text-sm font-medium rounded-full bg-blue-100 text-blue-700">
                    {t('progress.personCount', { count: c.inProgressLearnerCount })}
                  </span>
                </td>
                <td className="px-6 py-4 text-center">
                  <span className="inline-flex px-2.5 py-0.5 text-sm font-medium rounded-full bg-green-100 text-green-700">
                    {t('progress.personCount', { count: c.completedLearnerCount })}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// 상품별 뷰
function ProductView({ products }: { products: ProductProgressReport[] }) {
  const { t } = useTranslation('admin')
  if (products.length === 0) {
    return <div className="py-8 text-center text-gray-500">{t('progress.product.empty')}</div>
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm overflow-x-auto border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[240px] relative after:absolute after:right-0 after:top-0 after:bottom-0 after:w-px after:bg-gray-200">{t('progress.product.nameAndCurriculum')}</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Licensed</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t('progress.product.inProgress')}</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t('progress.product.completed')}</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t('progress.product.totalEnrolled')}</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t('progress.product.completionRate')}</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t('progress.product.enrollmentRate')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {products.map((p) => (
              <tr key={p.productId} className="group/row hover:bg-gray-50">
                <td className="sticky left-0 z-10 bg-white px-6 py-4 text-sm min-w-[240px] relative after:absolute after:right-0 after:top-0 after:bottom-0 after:w-px after:bg-gray-200 group-hover/row:bg-gray-50">
                  <div className="font-medium text-gray-900">{p.productName}</div>
                  {(p.curriculumTitles ?? []).length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {(p.curriculumTitles ?? []).map((title, i) => (
                        <div key={i} className="text-xs text-indigo-600">{title}</div>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-center text-gray-700 whitespace-nowrap">
                  {(p.licensedSeats ?? 0) > 0 ? t('progress.personCount', { count: p.licensedSeats }) : t('progress.product.unlimited')}
                </td>
                <td className="px-6 py-4 text-center whitespace-nowrap">
                  <span className="inline-flex px-2.5 py-0.5 text-sm font-medium rounded-full bg-blue-100 text-blue-700">
                    {t('progress.personCount', { count: p.inProgressCount })}
                  </span>
                </td>
                <td className="px-6 py-4 text-center whitespace-nowrap">
                  <span className="inline-flex px-2.5 py-0.5 text-sm font-medium rounded-full bg-green-100 text-green-700">
                    {t('progress.personCount', { count: p.completedCount })}
                  </span>
                </td>
                <td className="px-6 py-4 text-center whitespace-nowrap">
                  <span className="inline-flex px-2.5 py-0.5 text-sm font-medium rounded-full bg-purple-100 text-purple-700">
                    {t('progress.personCount', { count: p.totalEnrolled ?? ((p.inProgressCount ?? 0) + (p.completedCount ?? 0)) })}
                  </span>
                </td>
                <td className="px-6 py-4 text-center whitespace-nowrap">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="overflow-hidden w-16 h-2 bg-gray-200 rounded-full">
                      <div
                        className="h-full bg-green-500 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(p.completionRate ?? 0, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{(p.completionRate ?? 0).toFixed(1)}%</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-center whitespace-nowrap">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="overflow-hidden w-16 h-2 bg-gray-200 rounded-full">
                      <div
                        className="h-full bg-indigo-500 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(p.enrollmentRate ?? 0, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{(p.enrollmentRate ?? 0).toFixed(1)}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// 클래스별 뷰
function LcClassView({ classDetails, onNavigateToLearners }: {
  classDetails: DashboardClassDetail[]
  onNavigateToLearners: (classId: number) => void
}) {
  const { t } = useTranslation('admin')
  if (classDetails.length === 0) {
    return <div className="py-8 text-center text-gray-500">{t('progress.class.empty')}</div>
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {classDetails.map((detail) => (
          <div key={detail.classId} className="p-5 rounded-lg border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <div className="flex gap-2 items-center">
                <GraduationCap className="w-5 h-5 text-purple-600" />
                <h3 className="font-semibold text-gray-900">{detail.className}</h3>
              </div>
              <button
                onClick={() => onNavigateToLearners(detail.classId)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
              >
                <Users className="w-3.5 h-3.5" />
                {t('progress.class.learnerList')}
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="flex items-center gap-1.5">
                <BookOpen className="w-4 h-4 text-gray-400" />
                <span className="text-gray-500">{t('progress.class.courses')}</span>
                <span className="font-medium text-gray-900">{t('progress.class.countUnit', { count: detail.courseCount })}</span>
              </div>
              <div>
                <span className="text-gray-500">{t('progress.class.learners')}</span>
                <span className="ml-1 font-medium text-gray-900">{t('progress.personCount', { count: detail.learnerCount })}</span>
              </div>
              <div>
                <span className="text-gray-500">{t('progress.class.notStarted')}</span>
                <span className="ml-1 font-medium text-gray-900">{t('progress.personCount', { count: detail.notStartedCount })}</span>
              </div>
            </div>

            <div className="mt-3 space-y-2">
              <div>
                <div className="flex justify-between mb-1 text-sm">
                  <span className="text-gray-500">{t('progress.class.progressRate')}</span>
                  <span className="font-medium text-blue-600">{detail.progressRate.toFixed(1)}%</span>
                </div>
                <div className="overflow-hidden h-2 bg-gray-200 rounded-full">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(detail.progressRate, 100)}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-3 mt-3 text-sm border-t border-gray-100">
              <div>
                <span className="text-gray-500">{t('progress.class.completedLearners')}</span>
                <span className="ml-1 font-medium text-green-600">{t('progress.personCount', { count: detail.completedCount })}</span>
                <span className="ml-1 text-gray-400">({detail.completionRate.toFixed(1)}%)</span>
              </div>
              <div>
                <span className="text-gray-500">{t('progress.class.moduleQuizAccuracy')}</span>
                <span className="ml-1 font-medium text-orange-600">
                  {detail.moduleQuizAccuracy != null ? `${detail.moduleQuizAccuracy.toFixed(1)}%` : '-'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">{t('progress.class.aiqGrowthRate')}</span>
                <span className="ml-1 font-medium text-purple-600">
                  {detail.aiqGrowthRate != null ? `${detail.aiqGrowthRate.toFixed(1)}%` : '-'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// 학습자별 뷰
function LearnerView({ initialClassId, onClassIdConsumed }: {
  initialClassId?: number
  onClassIdConsumed?: () => void
}) {
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const canBypassAiqReportGate = isDevToolsVisible(user?.email)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [selectedClassId, setSelectedClassId] = useState<number | undefined>(initialClassId)
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [sortField, setSortField] = useState<'fullName' | 'email' | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  // initialClassId가 변경되면 적용
  useEffect(() => {
    if (initialClassId !== undefined) {
      setSelectedClassId(initialClassId)
      onClassIdConsumed?.()
    }
  }, [initialClassId])
  const [selectedLearner, setSelectedLearner] = useState<{userId: number, classProductId: number, fullName: string, className: string} | null>(null)

  const handleSearch = () => {
    setAppliedSearch(searchKeyword)
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleSort = (field: 'fullName' | 'email') => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const SortIcon = ({ field }: { field: 'fullName' | 'email' }) => {
    if (sortField !== field) return <ArrowUpDown className="inline w-3 h-3 ml-1 opacity-40" />
    return sortDirection === 'asc'
      ? <ArrowUp className="inline w-3 h-3 ml-1" />
      : <ArrowDown className="inline w-3 h-3 ml-1" />
  }

  // 필터 데이터 로드
  const { data: classesData } = useQuery({
    queryKey: ['lcClasses'],
    queryFn: () => lcClassApi.getAll(),
  })

  // 학습자 데이터 로드 (1회 호출로 통합)
  const { data: learnersResponse, isLoading } = useQuery({
    queryKey: ['learnerProgress', appliedSearch, selectedClassId, selectedStatus],
    queryFn: () => reportApi.getLearnerProgressDetail({
      search: appliedSearch || undefined,
      classId: selectedClassId,
      status: selectedStatus === 'all' ? undefined : selectedStatus,
    }),
  })

  const classes = classesData?.data?.data || []
  const responseData = learnersResponse?.data?.data
  const learners: LearnerProgressDetail[] = responseData?.learners || []
  const totalCount = responseData?.totalCount ?? 0
  const classFilteredCount = responseData?.classFilteredCount

  // 정렬 적용
  const sortedLearners = useMemo(() => {
    if (!sortField) return learners
    return [...learners].sort((a, b) => {
      const aVal = a[sortField] ?? ''
      const bVal = b[sortField] ?? ''
      const cmp = aVal.localeCompare(bVal, 'ko')
      return sortDirection === 'asc' ? cmp : -cmp
    })
  }, [learners, sortField, sortDirection])

  const handleAiqReport = (learner: LearnerProgressDetail) => {
    if (!canBypassAiqReportGate && isAiqReportDateLocked()) {
      toast.info('AIQ 리포트는 아직 제공되지 않습니다.')
      return
    }
    if (!learner.classProductId) {
      toast.warning(t('progress.learner.toast.noClassProduct'))
      return
    }
    const params = new URLSearchParams({
      userId: String(learner.userId),
      classProductId: String(learner.classProductId),
      name: learner.fullName,
    })
    navigate(`/admin/aiq-learner-report?${params}`)
  }

  const handleDetail = (learner: LearnerProgressDetail) => {
    if (!learner.classProductId) {
      toast.warning(t('progress.learner.toast.noClassProduct'))
      return
    }
    setSelectedLearner({
      userId: learner.userId,
      classProductId: learner.classProductId,
      fullName: learner.fullName,
      className: learner.className,
    })
  }

  const statusLabel = (status: string) => {
    switch (status) {
      case 'completed': return t('progress.learner.status.completed')
      case 'in_progress': return t('progress.learner.status.inProgress')
      case 'not_started': return t('progress.learner.status.notStarted')
      default: return status
    }
  }

  const statusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700'
      case 'in_progress': return 'bg-blue-100 text-blue-700'
      case 'not_started': return 'bg-gray-100 text-gray-500'
      default: return 'bg-gray-100 text-gray-500'
    }
  }

  return (
    <div className="space-y-4">
      {/* Search + Button */}
      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder={t('progress.learner.searchPlaceholder')}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <button
          onClick={handleSearch}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 whitespace-nowrap"
        >
          {t('common.search')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <select
          value={selectedClassId ?? ''}
          onChange={(e) => setSelectedClassId(e.target.value ? Number(e.target.value) : undefined)}
          className="px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">{t('progress.learner.classAll')}</option>
          {classes.map((c: any) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="all">{t('progress.learner.statusAll')}</option>
          <option value="not_started">{t('progress.learner.status.notStarted')}</option>
          <option value="in_progress">{t('progress.learner.status.inProgress')}</option>
          <option value="completed">{t('progress.learner.status.completed')}</option>
        </select>
      </div>

      {/* Summary + Table */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">{t('common.loading')}</div>
        </div>
      ) : (
        <>
        {(() => {
          const shown = learners.length
          const className = selectedClassId
            ? (classes.find((c: any) => c.id === selectedClassId)?.name ?? t('progress.learner.classLabel'))
            : null
          const hasStatusFilter = selectedStatus !== 'all'

          let total: number
          let description: string

          if (className && hasStatusFilter) {
            total = classFilteredCount ?? shown
            description = t('progress.learner.summary.classAndStatus', { class: className, total, count: shown, status: statusLabel(selectedStatus) })
          } else if (className) {
            total = totalCount
            description = t('progress.learner.summary.classOnly', { total, class: className, count: shown })
          } else if (hasStatusFilter) {
            total = totalCount
            description = t('progress.learner.summary.statusOnly', { total, count: shown, status: statusLabel(selectedStatus) })
          } else {
            total = totalCount
            description = t('progress.learner.summary.all', { count: total })
          }

          if (total === 0 || appliedSearch) return null
          const percent = total > 0 ? Math.round((shown / total) * 100) : 0
          return (
            <div className="flex gap-3 items-center px-4 py-3 bg-green-50 rounded-lg border border-green-200">
              <span className="text-2xl font-bold text-green-700">{percent}%</span>
              <span className="text-sm text-green-600">({description})</span>
            </div>
          )
        })()}
        {learners.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            {appliedSearch ? t('progress.learner.noSearchResults') : t('progress.learner.noResults')}
          </div>
        ) : (
        <div className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="pl-4 pr-1 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap sticky left-0 z-20 bg-gray-50 w-[80px] min-w-[80px] cursor-pointer select-none hover:text-gray-700"
                    onClick={() => handleSort('fullName')}
                  >
                    {t('progress.learner.table.name')} <SortIcon field="fullName" />
                  </th>
                  <th className="pl-1 pr-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap sticky left-[80px] z-20 bg-gray-50 min-w-[120px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">{t('progress.learner.table.deptPosition')}</th>
                  <th className="px-4 py-3 text-xs font-medium text-left text-gray-500 uppercase whitespace-nowrap">{t('progress.learner.table.class')}</th>
                  <th className="px-4 py-3 text-xs font-medium text-center text-gray-500 uppercase whitespace-nowrap">{t('progress.learner.table.progressRate')}</th>
                  <th className="px-4 py-3 text-xs font-medium text-center text-gray-500 uppercase whitespace-nowrap">{t('progress.learner.table.lessonAccuracy')}</th>
                  <th className="px-4 py-3 text-xs font-medium text-center text-gray-500 uppercase whitespace-nowrap">{t('progress.learner.table.moduleQuizAccuracy')}</th>
                  <th className="px-4 py-3 text-xs font-medium text-center text-gray-500 uppercase whitespace-nowrap">Pre-AIQ</th>
                  <th className="px-4 py-3 text-xs font-medium text-center text-gray-500 uppercase whitespace-nowrap">Post-AIQ</th>
                  <th className="px-4 py-3 text-xs font-medium text-center text-gray-500 uppercase whitespace-nowrap">{t('progress.learner.table.aiqGrowthRate')}</th>
                  <th className="px-4 py-3 text-xs font-medium text-center text-gray-500 uppercase whitespace-nowrap">{t('progress.learner.table.status')}</th>
                  <th className="px-4 py-3 text-xs font-medium text-center text-gray-500 uppercase whitespace-nowrap">{t('progress.learner.table.actions')}</th>
                  <th
                    className="px-4 py-3 text-xs font-medium text-left text-gray-500 uppercase whitespace-nowrap cursor-pointer select-none hover:text-gray-700"
                    onClick={() => handleSort('email')}
                  >
                    {t('progress.learner.table.email')} <SortIcon field="email" />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedLearners.map((learner, idx) => (
                  <tr key={`${learner.userId}-${learner.classId}-${idx}`} className="group hover:bg-gray-50">
                    <td className="pl-4 pr-1 py-3 text-sm font-medium text-gray-900 whitespace-nowrap sticky left-0 z-10 bg-white group-hover:bg-gray-50 w-[80px] min-w-[80px]">{learner.fullName}</td>
                    <td className="pl-1 pr-4 py-3 text-sm text-gray-500 whitespace-nowrap sticky left-[80px] z-10 bg-white group-hover:bg-gray-50 min-w-[120px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                      {learner.departmentName || '-'} / {learner.position || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{learner.className}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <div className="overflow-hidden w-16 h-2 bg-gray-200 rounded-full">
                          <div
                            className="h-full rounded-full bg-primary-500"
                            style={{ width: `${learner.progressPercent}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">{learner.progressPercent.toFixed(0)}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-gray-900 whitespace-nowrap">
                      {learner.lessonQuizAccuracy != null ? `${learner.lessonQuizAccuracy.toFixed(1)}%` : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-gray-900 whitespace-nowrap">
                      {learner.checkupQuizAccuracy != null ? `${learner.checkupQuizAccuracy.toFixed(1)}%` : '-'}
                    </td>
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                        learner.preAiqCompleted ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
                      }`}>
                        {learner.preAiqCompleted ? 'O' : 'X'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                        learner.postAiqCompleted ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
                      }`}>
                        {learner.postAiqCompleted ? 'O' : 'X'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-gray-500 whitespace-nowrap">
                      {learner.aiqGrowthRate != null ? `${learner.aiqGrowthRate.toFixed(1)}%` : '-'}
                    </td>
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusBadgeClass(learner.learningStatus)}`}>
                        {statusLabel(learner.learningStatus)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleAiqReport(learner)}
                          className="px-2 py-1 text-xs font-medium rounded text-primary-600 bg-primary-50 hover:bg-primary-100"
                          title={t('progress.learner.aiqReport')}
                        >
                          {t('progress.learner.aiqReport')}
                        </button>
                        <button
                          onClick={() => handleDetail(learner)}
                          className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-50 rounded hover:bg-gray-100"
                          title={t('progress.learner.detail')}
                        >
                          {t('progress.learner.detail')}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{learner.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}
        </>
      )}

      {selectedLearner && (
        <LearnerProgressDetailModal
          userId={selectedLearner.userId}
          classProductId={selectedLearner.classProductId}
          learnerName={selectedLearner.fullName}
          className={selectedLearner.className}
          isOpen={!!selectedLearner}
          onClose={() => setSelectedLearner(null)}
        />
      )}
    </div>
  )
}
